import { databaseService } from './DatabaseService';
import { Attachment, ChecklistItem, Inspection, InspectionGroup } from '@/types/model/inspection';

export interface CachedModuleSummary {
  contractId: number;
  contractName: string;
  totalCached: number;
  pendingSync: number;
}

class DatabaseCachingService {
  private cacheChangeListeners: (() => void)[] = [];

  public addCacheChangeListener(listener: () => void): () => void {
    this.cacheChangeListeners.push(listener);
    return () => {
      const index = this.cacheChangeListeners.indexOf(listener);
      if (index > -1) this.cacheChangeListeners.splice(index, 1);
    };
  }

  public notifyCacheChanged(): void {
    this.cacheChangeListeners.forEach(l => {
      try { l(); } catch (e) { console.error('Error in cache change listener:', e); }
    });
  }

  public async clearCache(keepEssential = true): Promise<void> {
    try {
      if (!keepEssential) {
        // Clear all cached data
        const tables = [
          'cached_attachments', 'cached_checklist_items',
          'cached_inspections', 'cached_contracts' 
        ];

        for (const table of tables) {
          await databaseService.delete(table, '1=1', []);
        }
      }

      // Clear expired cache
      await databaseService.clearExpiredCache();
      await databaseService.clearOldSyncQueue();
      
      // Vacuum database
      await databaseService.vacuum();
      
      console.log('🧹 Cache cleared successfully');
      this.notifyCacheChanged();
    } catch (error) {
      console.error('Error clearing cache:', error);
      throw error;
    }
  }

  public async clearCachedContractInspections(contractId: number): Promise<void> {
    try {
      // Clear cached inspections related to the specified contract, cascade to checklist items and attachments if needed
      await databaseService.delete('cached_checklist_items', 'inspection_result_id IN (SELECT inspection_result_id FROM cached_inspections WHERE contract_id = ?)', [contractId]);
      await databaseService.delete('cached_attachments', 'parent_id IN (SELECT inspection_result_id FROM cached_inspections WHERE contract_id = ?) AND parent_type = ?', [contractId, 'inspection']);
      await databaseService.delete('cached_inspections', 'contract_id = ?', [contractId]);
      console.log('🧹 Cached contract inspections cleared successfully');
      this.notifyCacheChanged();
    } catch (error) {
      console.error('Error clearing cached contract inspections:', error);
      throw error;
    }
  }

  public async cacheInspections(inspectionGroups: InspectionGroup[], currentContract: { Id: number; Name: string }): Promise<boolean> {
    try {
      const cacheContractResult = await this.cacheContracts([{ Id: currentContract.Id, Name: currentContract.Name }]);
      if (!cacheContractResult) {
        console.warn('Failed to cache contract groups before caching inspections');
      }

      console.log('Caching inspections:', inspectionGroups);
      // TODO: Clear existing cached inspections for the current contract before caching new data?
      // Note: Depending on the desired caching strategy, you might want to clear only inspections related to the current contract instead of all cached inspections.
      // OR skip if inspections are already cached for the current contract to allow for offline access without needing to re-cache every time.
      //await databaseService.delete('cached_inspections', '1=1', []);
      for (const group of inspectionGroups) {
        for (const lineItem of group.lineItems ?? []) {
          const subItemById = new Map((lineItem.subItems ?? []).map(subItem => [subItem.id, subItem]));
          for (const inspection of lineItem.inspections ?? []) {
            const subItem = subItemById.get(inspection.inspectionResultId);
            if (!subItem || subItem.status !== 'in-progress') {
              continue;
            }

            await databaseService.insert('cached_inspections', {
              contract_id: currentContract.Id,
              inspection_result_id: inspection.inspectionResultId,
              group_id: group.id,
              group_identifier: group.groupIdentifier,
              group_title: group.title,
              line_item_id: inspection.lineItemId,
              line_item_title: inspection.title, 
              line_item: inspection.lineItem,
              site_id: inspection.siteId,
              site_name: inspection.siteName,
              user_id: inspection.userId,
              inspector_name: inspection.inspectorName,
              area_of_expertise: inspection.areaOfExpertise,
              area_of_expertise_id: inspection.areaOfExpertiseId,
              composite_score: inspection.compositeScore,
              time_sensitivity: subItem.timeSensitivity,
              status: subItem.status,
              inspected_on: inspection.inspectedOn,
              scheduled_date: subItem.scheduledDate,
              line_item_primary_weight: inspection.inspectionPlanBackground.LineItemPrimaryWeight,
              line_item_alternate_weight: inspection.inspectionPlanBackground.LineItemAlternateWeight,
              time_to_inspect: inspection.inspectionPlanBackground.TimeToInspect,
              method_of_inspection: inspection.inspectionPlanBackground.MethodOfInspection,
              sample_size: inspection.inspectionPlanBackground.SampleSize,
              sample_selection: inspection.inspectionPlanBackground.SampleSelection,
              additional_instructions: inspection.inspectionPlanBackground.AdditionalInstructions,
              major_requirement: inspection.lineItemDetail.MajorRequirement,
              related_info: inspection.lineItemDetail.RelatedInfo,
              performance_standard: inspection.lineItemDetail.PerformanceStandard,
              estimated_quantity: inspection.lineItemDetail.EstimatedQuantity,
              cached_at: new Date().toISOString()
            });

            const cacheChecklistItemsResult = await this.replaceCachedChecklistItems(inspection.inspectionResultId, inspection.checklistItems);
            if (!cacheChecklistItemsResult) {
              console.warn(`Failed to cache checklist items for inspection ${inspection.inspectionResultId}`);
            }
          }
        }
      }
      this.notifyCacheChanged();
      return true;
    } catch (error) {
      console.error('Error caching inspections:', error);
      return false;
    }
  }

  async cacheAttachments(parentType: string, parentId: number, attachments: Attachment[]): Promise<boolean> {
    try {
      await databaseService.delete('cached_attachments', 'parent_type = ? AND parent_id = ?', [parentType, parentId]);
      for (const attachment of attachments) {
        await databaseService.insert('cached_attachments', {
          parent_type: parentType,
          parent_id: parentId,
          description: attachment.Description,
          link: attachment.Link,
          file_id: attachment.FileId,
          file_name: attachment.Filename,
          cached_at: new Date().toISOString()
        });
      }
      return true;
    } catch (error) {
      console.error('Error caching attachments:', error);
      return false;
    }
  }

  public async cacheContracts(groups: { Id: number; Name: string }[]): Promise<boolean> {
    try {
      //TODO: Similar to inspections, consider whether to clear all cached contract groups or only those related to the current contract. This depends on how you want to manage cached data and offline access.
      // await databaseService.delete('cached_contracts', '1=1', []);
      for (const group of groups) {
        await databaseService.insert('cached_contracts', {
          contract_id: group.Id,
          name: group.Name,
          cached_at: new Date().toISOString()
        });
      }
      return true;
    } catch (error) {
      console.error('Error caching contract groups:', error);
      return false;
    }
  }

  public  async updateCachedInspection(inspection: Inspection): Promise<boolean> {
    try {
      // Update only checklist items and attachments for the inspection result, assuming other details remain unchanged. Alternatively, you could update all fields if needed.
      await this.replaceCachedChecklistItems(inspection.inspectionResultId, inspection.checklistItems);
      // Stamp updated_at so getAllUpdatedCachedInspections can detect locally-modified records
      await databaseService.update(
        'cached_inspections',
        { updated_at: new Date().toISOString() },
        'inspection_result_id = ?',
        [inspection.inspectionResultId]
      );
      this.notifyCacheChanged();
      return true;
    } catch (error) {
      console.error('Error updating cached inspection:', error);
      return false;
    }
  }

  private async replaceCachedChecklistItems(inspectionResultId: number, checklistItems: ChecklistItem[]): Promise<boolean> {
    try {
      // For simplicity, delete existing checklist items and re-insert updated ones. Alternatively, you could implement a more sophisticated update logic to only update changed items.
      await databaseService.delete('cached_checklist_items', 'inspection_result_id = ?', [inspectionResultId]);
      for (const item of checklistItems) {
        await databaseService.insert('cached_checklist_items', {
          inspection_result_id: inspectionResultId,
          checklist_item_id: item.Id,
          text: item.Text,
          order_index: item.Order,
          score: item.Score,
          comment: item.Comment,
          weight: item.Weight,
          results_analysis: item.ResultsAnalysis,
          cached_at: new Date().toISOString()
        });

        if (item.Attachments && item.Attachments.length > 0) {
          const cacheAttachmentsResult = await this.cacheAttachments(`checklist_item`, item.Id, item.Attachments);
          if (!cacheAttachmentsResult) {
            console.warn(`Failed to cache attachments for checklist item ${item.Id}`);
          }
        }
      }
      return true;
    } catch (error) {
      console.error('Error updating cached checklist items:', error);
      return false;
    }
  }

  public async getAllUpdatedCachedInspections(contractId: number): Promise<Inspection[]> {
    try {
      const inspections = await databaseService.select('cached_inspections', 'contract_id = ? and updated_at > cached_at', [contractId]);
      return inspections as Inspection[];
    } catch (error) {
      console.error('Error fetching updated cached inspections:', error);
      return [];
    }
  }

  public async getCachedModuleSummaries(): Promise<CachedModuleSummary[]> {
    try {
      const contractRows = await databaseService.select('cached_contracts', undefined, []);
      // Deduplicate by contract_id (insert may not guard against duplicates)
      const seen = new Map<number, string>();
      for (const row of contractRows) {
        const id = row.contract_id as number;
        if (!seen.has(id)) {
          seen.set(id, row.name as string);
        }
      }

      const summaries: CachedModuleSummary[] = [];
      for (const [contractId, contractName] of seen) {
        const allRows = await databaseService.select('cached_inspections', 'contract_id = ?', [contractId]);
        const pendingRows = allRows.filter((r: any) => r.updated_at && r.updated_at > r.cached_at);
        summaries.push({ contractId, contractName, totalCached: allRows.length, pendingSync: pendingRows.length });
      }
      return summaries;
    } catch (error) {
      console.error('Error fetching cached module summaries:', error);
      return [];
    }
  }
}

export const databaseCachingService = new DatabaseCachingService();
export default databaseCachingService;